import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { create } from 'zustand';
import { isDiffByPercent } from '../components/utils/numbers';
import { useSlippageStore } from '../stores/slippageStore';
import { sleep } from '@layerswap/utils';
import { useSettingsState } from '../context/settings';
import { resolveExtendedRoutePlan } from '../lib/extendedRoutes/registry';
import { usesDepository } from '@layerswap/widget-types';
import { transformLimitsForExtendedRoute, transformQuoteForExtendedRoute } from '../lib/extendedRoutes/transforms';
import { isPositiveDecimal } from '../lib/extendedRoutes/amounts';
import { LayerswapApiClient } from '../lib/apiClients';
import { useSelectedAccount } from '../context/swapAccounts';
import { useGaslessPreferenceStore } from '../stores/gaslessPreferenceStore';
import { isGaslessCapableRoute } from '../helpers/gasless';
import { Address } from '../lib/address/Address';
import { wantsFrontendSwap } from '../helpers/swapFlow';
const apiClient = new LayerswapApiClient();
export function useQuoteData(formValues, options = { skipLimits: false, refreshInterval: 20000 }) {
    const { fromCurrency, toCurrency, from, to, amount, refuel, depositMethod, destinationAddress } = formValues || {};
    const { skipLimits, refreshInterval } = options;
    const [debouncedAmount, setDebouncedAmount] = useState(amount);
    const [isDebouncing, setIsDebouncing] = useState(false);
    const { slippage } = useSlippageStore();
    useEffect(() => {
        if (amount === debouncedAmount) {
            setIsDebouncing(false);
            return;
        }
        setIsDebouncing(true);
        const handler = setTimeout(() => {
            setDebouncedAmount(amount);
            setIsDebouncing(false);
        }, 300);
        return () => {
            clearTimeout(handler);
        };
    }, [amount]);
    const use_deposit_address = depositMethod === 'wallet' ? false : true;
    const useFrontendSwap = wantsFrontendSwap({
        depositMethod,
        sourceNetwork: from,
        destinationNetwork: to,
    });
    // Extended source (e.g. Hyperliquid): the backend doesn't know this source,
    // so quote/limits are fetched against the real route it maps to.
    const { networks, sourceRoutes } = useSettingsState();
    const extendedPlan = useMemo(() => resolveExtendedRoutePlan({
        sourceNetworkName: from,
        sourceTokenSymbol: fromCurrency,
        destinationNetworkName: to,
        destinationTokenSymbol: toCurrency,
        sourceAmount: debouncedAmount,
        availableRoutes: sourceRoutes,
    }), [from, fromCurrency, to, toCurrency, debouncedAmount, sourceRoutes]);
    const extendedMapping = extendedPlan?.mapping;
    const isBridge = !!extendedPlan;
    const effectiveFrom = isBridge ? extendedMapping.real.networkName : from;
    const effectiveFromToken = isBridge ? extendedMapping.real.tokenSymbol : fromCurrency;
    const effectiveUseDepositAddress = extendedPlan ? !usesDepository(extendedMapping.provider) : use_deposit_address;
    const extendedNetworkObj = useMemo(() => extendedMapping ? networks.find(n => n.name === extendedMapping.extendedNetworkName) : undefined, [networks, extendedMapping]);
    const extendedTokenObj = useMemo(() => extendedNetworkObj?.tokens.find(t => t.symbol === extendedMapping?.extendedTokenSymbol), [extendedNetworkObj, extendedMapping]);
    // Mirror the swap-create gasless gate so quote/limits reflect the gasless route.
    const gaslessEnabled = useGaslessPreferenceStore(s => s.gaslessEnabled);
    const selectedSourceAccount = useSelectedAccount("from", from);
    const sourceRouteToken = useMemo(() => sourceRoutes?.find(r => r.name === from)?.tokens?.find(t => t.symbol === fromCurrency), [sourceRoutes, from, fromCurrency]);
    const sourceIsSupported = !!selectedSourceAccount?.walletAsSourceSupportedNetworks?.some(n => n === from);
    const sourceAddress = sourceIsSupported ? selectedSourceAccount?.address : undefined;
    const useGasless = !isBridge && gaslessEnabled && isGaslessCapableRoute({
        depositMethod,
        supportsGaslessDeposit: sourceRouteToken?.supports_gasless_deposit,
        sourceTokenContract: sourceRouteToken?.contract,
        gaslessStandard: sourceRouteToken?.gasless_standard,
        sourceIsSupported,
        sourceAddress: selectedSourceAccount?.address,
    });
    const limitsURL = (!skipLimits && from && to && depositMethod && toCurrency && fromCurrency) ?
        buildLimitsUrl({
            sourceNetwork: effectiveFrom,
            sourceToken: effectiveFromToken,
            destinationNetwork: to,
            destinationToken: toCurrency,
            useDepositAddress: effectiveUseDepositAddress,
            useFrontendSwap,
            refuel,
            useGasless,
            destinationAddress,
        }) : null;
    const { data: amountRange, mutate: mutateLimits, isValidating: limitsValidating, error: limitsError } = useSWR(limitsURL, apiClient.fetcher, {
        refreshInterval: (refreshInterval || refreshInterval == 0) ? refreshInterval : 20000,
        dedupingInterval: 5000
    });
    const hasQuoteParams = from && to && depositMethod && toCurrency && fromCurrency && debouncedAmount;
    // Bridge mode fetches the backend quote for the truncated real amount (A - fee).
    const effectiveAmount = isBridge ? extendedPlan.realAmount : debouncedAmount;
    const quoteURL = (hasQuoteParams && !isDebouncing && (!isBridge || (effectiveAmount && isPositiveDecimal(effectiveAmount))))
        ? buildQuoteUrl({
            sourceNetwork: effectiveFrom,
            sourceToken: effectiveFromToken,
            destinationNetwork: to,
            destinationToken: toCurrency,
            amount: effectiveAmount || 0,
            refuel: !!refuel,
            useDepositAddress: effectiveUseDepositAddress,
            useFrontendSwap,
            slippage,
            useGasless,
            destinationAddress,
            sourceAddress,
        })
        : null;
    const { cache } = useSWRConfig();
    const isQuoteLoading = useLoadingStore((state) => state.isLoading);
    // Remember the last settled quote we emitted, so that while the next one loads we don't flash stale data after an empty state (e.g. a route that errored).
    const lastSettledQuoteRef = useRef(undefined);
    //TODO: implement middleware that handles the delay logic
    const quoteFetchWrapper = useCallback(async (url) => {
        const { setLoading, key, setKey } = useLoadingStore.getState();
        try {
            if (key !== url) {
                setLoading(true);
            }
            const previousData = cache.get(url)?.data;
            const newData = await apiClient.fetcher(url);
            if (previousData?.data?.quote && isDiffByPercent(previousData?.data?.quote.receive_amount, newData.data?.quote.receive_amount, 2)) {
                const { setLoading } = useLoadingStore.getState();
                setLoading(true);
                await sleep(3500);
            }
            setKey(url);
            setLoading(false);
            return newData;
        }
        catch (error) {
            if (error.response?.data?.error?.code === "VALIDATION_ERROR") {
                useSlippageStore.getState().clearSlippage();
            }
            setLoading(false);
            setKey(null);
            throw error;
        }
    }, [cache]);
    const { data: quote, mutate: mutateFee, error: quoteError, isLoading: swrIsLoading } = useSWR(quoteURL, quoteFetchWrapper, {
        refreshInterval: (refreshInterval || refreshInterval == 0) ? refreshInterval : 42000,
        dedupingInterval: 5000,
        keepPreviousData: true,
    });
    const quoteData = quote?.data;
    const hasValidAmount = !!debouncedAmount && isPositiveDecimal(String(debouncedAmount));
    const isTransitioning = swrIsLoading || isDebouncing || amount !== debouncedAmount;
    const resolvedQuote = (quoteError || !hasQuoteParams || !hasValidAmount) ? undefined : quoteData;
    if (!isTransitioning)
        lastSettledQuoteRef.current = resolvedQuote;
    const suppressStale = isTransitioning && lastSettledQuoteRef.current === undefined;
    // Re-denominate the backend quote so the source side reads as the extended route.
    let finalQuote = suppressStale ? undefined : resolvedQuote;
    if (extendedMapping && hasValidAmount && debouncedAmount) {
        const sourceAmount = String(debouncedAmount);
        if (isBridge && finalQuote && extendedNetworkObj && extendedTokenObj) {
            finalQuote = transformQuoteForExtendedRoute(finalQuote, extendedMapping, extendedNetworkObj, extendedTokenObj, sourceAmount);
        }
    }
    let minAllowedAmount = amountRange?.data?.min_amount;
    let maxAllowedAmount = amountRange?.data?.max_amount;
    let minAllowedAmountInUsd = amountRange?.data?.min_amount_in_usd;
    let maxAllowedAmountInUsd = amountRange?.data?.max_amount_in_usd;
    if (isBridge && extendedMapping) {
        const transformed = transformLimitsForExtendedRoute(amountRange?.data, extendedMapping);
        minAllowedAmount = transformed?.min_amount;
        maxAllowedAmount = transformed?.max_amount;
        minAllowedAmountInUsd = transformed?.min_amount_in_usd;
        maxAllowedAmountInUsd = transformed?.max_amount_in_usd;
    }
    return {
        minAllowedAmount,
        maxAllowedAmount,
        minAllowedAmountInUsd,
        maxAllowedAmountInUsd,
        quote: finalQuote,
        quoteTokenPrices: (finalQuote?.quote && !suppressStale) ? {
            source_token: finalQuote.quote.source_token,
            destination_token: finalQuote.quote.destination_token,
        } : undefined,
        isQuoteLoading: isQuoteLoading || isTransitioning,
        isDebouncing: isDebouncing || amount !== debouncedAmount,
        quoteError,
        limitsError,
        mutateFee,
        mutateLimits,
        limitsValidating,
    };
}
export function validDestinationAddress(address, network) {
    return address && Address.isValid(address, network) ? address : undefined;
}
export function transformFormValuesToQuoteArgs(values) {
    return {
        amount: values.amount,
        from: values.from?.name,
        depositMethod: values.depositMethod,
        fromCurrency: values.fromAsset?.symbol,
        to: values.to?.name,
        toCurrency: values.toAsset?.symbol,
        refuel: values.refuel,
        destinationAddress: validDestinationAddress(values.destination_address, values.to),
    };
}
export function transformSwapDataToQuoteArgs(swapData, refuel) {
    return {
        refuel,
        amount: swapData?.requested_amount,
        from: swapData?.source_network.name,
        depositMethod: swapData?.use_deposit_address ? 'deposit_address' : 'wallet',
        fromCurrency: swapData?.source_token.symbol,
        to: swapData?.destination_network.name,
        toCurrency: swapData?.destination_token.symbol,
        destinationAddress: validDestinationAddress(swapData?.destination_address, swapData?.destination_network),
    };
}
export function buildQuoteUrl(args) {
    const { sourceNetwork, sourceToken, destinationNetwork, destinationToken, amount, refuel, useDepositAddress, slippage, useGasless = false, destinationAddress, sourceAddress, } = args;
    const params = new URLSearchParams({
        source_network: sourceNetwork,
        source_token: sourceToken,
        destination_network: destinationNetwork,
        destination_token: destinationToken,
        amount: String(amount),
        refuel: String(!!refuel),
        use_deposit_address: useDepositAddress ? 'true' : 'false',
        use_frontend_swap: 'true',
        use_gasless: String(useGasless),
    });
    if (slippage !== undefined) {
        params.append('slippage', String(slippage));
    }
    if (destinationAddress) {
        params.append('destination_address', destinationAddress);
    }
    if (sourceAddress) {
        params.append('source_address', sourceAddress);
    }
    return `/quote?${params.toString()}`;
}
export const getLimits = async (swapValues) => {
    const { sourceToken, sourceNetwork, destinationNetwork, destinationToken, refuel, useDepositAddress, useFrontendSwap, useGasless, destinationAddress } = swapValues || {};
    if (!sourceNetwork || !destinationNetwork || useDepositAddress === undefined || !destinationToken || !sourceToken)
        return { minAllowedAmount: undefined, maxAllowedAmount: undefined };
    const url = buildLimitsUrl({
        sourceNetwork,
        sourceToken,
        destinationNetwork,
        destinationToken,
        useDepositAddress,
        useFrontendSwap,
        useGasless,
        refuel,
        destinationAddress
    });
    const response = await apiClient.fetcher(url);
    return {
        minAllowedAmount: response?.data?.min_amount,
        maxAllowedAmount: response?.data?.max_amount
    };
};
export function buildLimitsUrl({ sourceNetwork, sourceToken, destinationNetwork, destinationToken, useDepositAddress, refuel = false, useGasless = false, destinationAddress }) {
    if (!sourceNetwork || !sourceToken || !destinationNetwork || !destinationToken) {
        throw new Error("Invalid parameters for building limits URL");
    }
    const params = new URLSearchParams({
        source_network: sourceNetwork,
        source_token: sourceToken,
        destination_network: destinationNetwork,
        destination_token: destinationToken,
        use_deposit_address: useDepositAddress ? 'true' : 'false',
        use_frontend_swap: 'true',
        use_gasless: String(useGasless),
        refuel: String(!!refuel),
    });
    if (destinationAddress) {
        params.append('destination_address', destinationAddress);
    }
    return `/limits?${params.toString()}`;
}
export const useLoadingStore = create((set) => ({
    key: null,
    setKey: (value) => set({ key: value }),
    isLoading: false,
    setLoading: (loading) => set({ isLoading: loading }),
}));
